import { useCallback } from 'react'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'

const PAGE_HEIGHT_MM = 297
const PAGE_WIDTH_MM = 210
const MARGIN_MM = 10
const SECTION_GAP_MM = 5
const JPEG_QUALITY = 0.7

/** Espaço entre o cabeçalho repetido e o início do conteúdo de cada página. */
const HEADER_GAP_MM = 3
/**
 * Altura reservada no pé de cada página para a linha divisória, o "powered by"
 * e o número da página. O conteúdo nunca invade essa faixa.
 */
const FOOTER_HEIGHT_MM = 10
const FOOTER_FONT_SIZE_PT = 8

/**
 * Limite conservador de dimensão de canvas nos browsers (o Chrome corta acima
 * de ~16k px por lado). Como o relatório é capturado de uma vez só, um exame
 * com muitas anotações pode passar disso — nesse caso a escala é reduzida em
 * vez de deixar o html2canvas devolver um canvas em branco.
 */
const MAX_CANVAS_SIDE_PX = 16384
const TARGET_SCALE = 2

// Helper para esperar todas as imagens carregarem
const waitForImagesToLoad = (element: HTMLElement): Promise<void> => {
  const images = Array.from(element.querySelectorAll('img'))
  const promises = images.map(
    img =>
      new Promise<void>(resolve => {
        if (img.complete) {
          resolve()
        } else {
          img.onload = () => resolve()
          img.onerror = () => resolve() // Resolve mesmo com erro para não bloquear
          // Timeout de segurança
          setTimeout(() => resolve(), 5000)
        }
      })
  )
  return Promise.all(promises).then(() => {})
}

/**
 * Recorta uma faixa horizontal do canvas capturado.
 *
 * O fundo é pintado de branco antes do recorte porque a faixa pode conter
 * regiões transparentes, e o JPEG converteria isso em preto.
 */
const cropCanvas = (
  source: HTMLCanvasElement,
  topPx: number,
  heightPx: number
): HTMLCanvasElement | null => {
  const target = document.createElement('canvas')
  target.width = source.width
  target.height = heightPx

  const ctx = target.getContext('2d')
  if (!ctx) return null

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, target.width, heightPx)
  ctx.drawImage(
    source,
    0,
    topPx,
    source.width,
    heightPx,
    0,
    0,
    source.width,
    heightPx
  )

  return target
}

export const useGeneratePDF = () => {
  const generatePDF = useCallback(
    async (elementId: string, filename: string) => {
      try {
        const element = document.getElementById(elementId)
        if (!element) {
          throw new Error('Element not found')
        }

        // Aguardar todas as imagens carregarem
        await waitForImagesToLoad(element)

        const pdf = new jsPDF({
          orientation: 'p',
          unit: 'mm',
          format: 'a4',
          compress: true // Ativar compressão do PDF
        })

        const imgWidth = PAGE_WIDTH_MM - 2 * MARGIN_MM

        // Captura única do relatório inteiro. O html2canvas não clona apenas o
        // elemento recebido: ele clona o documento todo num iframe e só então
        // localiza o alvo. Chamá-lo uma vez por seção (como era feito antes)
        // pagava esse clone N vezes — com o app inteiro montado por trás, era
        // o custo dominante da geração. Aqui o documento é rasterizado uma vez
        // e cada seção sai por recorte do canvas resultante.
        const scale = Math.min(
          TARGET_SCALE,
          MAX_CANVAS_SIDE_PX /
            Math.max(element.scrollWidth, element.scrollHeight)
        )

        const canvas = await html2canvas(element, {
          scale,
          useCORS: true,
          allowTaint: false,
          logging: false,
          backgroundColor: '#ffffff',
          // 0 aqui NÃO significa "não esperar": desliga o timeout e trava a
          // geração indefinidamente se alguma imagem não resolver.
          imageTimeout: 15000,
          removeContainer: true,
          foreignObjectRendering: false,
          // O relatório é montado como irmão do #root, então pular a árvore do
          // app dispensa o parse de todo o restante da página.
          ignoreElements: node => node.id === 'root'
        })

        // Razão entre o canvas e o layout em CSS px, usada para converter a
        // posição de cada bloco em coordenadas do canvas.
        const rootRect = element.getBoundingClientRect()
        const ratio = canvas.width / element.offsetWidth

        /** Recorta do canvas a faixa ocupada por um elemento do relatório. */
        const cropElement = (target: HTMLElement) => {
          const rect = target.getBoundingClientRect()
          const topPx = Math.max(
            0,
            Math.round((rect.top - rootRect.top) * ratio)
          )
          const heightPx = Math.min(
            Math.round(rect.height * ratio),
            canvas.height - topPx
          )
          if (heightPx <= 0) return null
          return cropCanvas(canvas, topPx, heightPx)
        }

        const toMm = (sectionCanvas: HTMLCanvasElement) =>
          (sectionCanvas.height * imgWidth) / sectionCanvas.width

        // Cabeçalho repetido em todas as páginas. É recortado uma vez e
        // desenhado no topo de cada página nova; o conteúdo começa abaixo dele.
        const headerElement =
          element.querySelector<HTMLElement>('[data-pdf-header]')
        const headerCanvas = headerElement ? cropElement(headerElement) : null
        const headerData = headerCanvas
          ? headerCanvas.toDataURL('image/jpeg', JPEG_QUALITY)
          : null
        const headerHeightMm = headerCanvas ? toMm(headerCanvas) : 0

        // Rodapé ("powered by"). Também é recortado uma vez e desenhado em
        // todas as páginas, junto com a numeração.
        const footerElement =
          element.querySelector<HTMLElement>('[data-pdf-footer]')
        const footerCanvas = footerElement ? cropElement(footerElement) : null
        const footerData = footerCanvas
          ? footerCanvas.toDataURL('image/jpeg', JPEG_QUALITY)
          : null
        const footerHeightMm = footerCanvas ? toMm(footerCanvas) : 0

        const contentTop =
          MARGIN_MM + (headerCanvas ? headerHeightMm + HEADER_GAP_MM : 0)
        const contentBottom = PAGE_HEIGHT_MM - MARGIN_MM - FOOTER_HEIGHT_MM
        const usableHeight = contentBottom - contentTop

        let currentY = contentTop

        /** Desenha o cabeçalho na página atual e posiciona o cursor abaixo. */
        const paintHeader = () => {
          if (headerData) {
            pdf.addImage(
              headerData,
              'JPEG',
              MARGIN_MM,
              MARGIN_MM,
              imgWidth,
              headerHeightMm,
              undefined,
              'FAST'
            )
          }
          currentY = contentTop
        }

        const addPage = () => {
          pdf.addPage()
          paintHeader()
        }

        /**
         * Posiciona uma seção já rasterizada no PDF, abrindo página nova quando
         * não couber no que resta da atual.
         */
        const addSectionCanvas = (sectionCanvas: HTMLCanvasElement) => {
          const imgHeight = toMm(sectionCanvas)

          if (imgHeight <= usableHeight) {
            if (currentY + imgHeight > contentBottom) {
              addPage()
            }

            // JPEG com compressão ao invés de PNG
            pdf.addImage(
              sectionCanvas.toDataURL('image/jpeg', JPEG_QUALITY),
              'JPEG',
              MARGIN_MM,
              currentY,
              imgWidth,
              imgHeight,
              undefined,
              'FAST'
            )
            currentY += imgHeight + SECTION_GAP_MM
            return
          }

          // Seção mais alta que uma página inteira: sem fatiar, o jsPDF
          // desenha a imagem transbordando e simplesmente corta o excesso —
          // o conteúdo do pé da seção some do relatório. Aqui ela é recortada
          // em pedaços de uma página cada.
          if (currentY > contentTop) {
            addPage()
          }

          const pxPerMm = sectionCanvas.height / imgHeight
          const slicePx = Math.max(1, Math.floor(usableHeight * pxPerMm))
          let offsetPx = 0

          while (offsetPx < sectionCanvas.height) {
            if (offsetPx > 0) {
              addPage()
            }

            const sliceHeightPx = Math.min(
              slicePx,
              sectionCanvas.height - offsetPx
            )
            const slice = cropCanvas(sectionCanvas, offsetPx, sliceHeightPx)
            if (!slice) break

            const sliceHeightMm = sliceHeightPx / pxPerMm
            pdf.addImage(
              slice.toDataURL('image/jpeg', JPEG_QUALITY),
              'JPEG',
              MARGIN_MM,
              contentTop,
              imgWidth,
              sliceHeightMm,
              undefined,
              'FAST'
            )

            currentY = contentTop + sliceHeightMm + SECTION_GAP_MM
            offsetPx += sliceHeightPx
          }
        }

        paintHeader()

        // Renderiza cada seção separadamente para evitar quebra no meio
        const sections =
          element.querySelectorAll<HTMLElement>('[data-pdf-section]')

        if (sections.length === 0) {
          // Fallback quando não há seções marcadas: pagina o canvas inteiro.
          addSectionCanvas(canvas)
        } else {
          for (const section of Array.from(sections)) {
            const sectionCanvas = cropElement(section)
            if (sectionCanvas) addSectionCanvas(sectionCanvas)
          }
        }

        // Rodapé: "powered by", linha divisória e "Página N de M". Só dá para
        // desenhar no fim, quando o total de páginas é conhecido.
        const totalPages = pdf.getNumberOfPages()
        const footerLineY = contentBottom + 2

        // O recorte do "powered by" tem a largura inteira do conteúdo, então
        // sai centralizado. Se não couber entre a linha e a margem, é reduzido
        // nas duas dimensões e continua centralizado.
        const footerScale = footerHeightMm
          ? Math.min(
              1,
              (PAGE_HEIGHT_MM - MARGIN_MM - footerLineY) / footerHeightMm
            )
          : 0
        const footerWidthMm = imgWidth * footerScale
        pdf.setFontSize(FOOTER_FONT_SIZE_PT)
        pdf.setTextColor(0, 0, 0)
        pdf.setDrawColor(51, 51, 51)
        pdf.setLineWidth(0.3)

        for (let page = 1; page <= totalPages; page += 1) {
          pdf.setPage(page)
          // Desenhado antes da linha e do texto: o fundo branco do JPEG
          // cobriria os dois.
          if (footerData) {
            pdf.addImage(
              footerData,
              'JPEG',
              MARGIN_MM + (imgWidth - footerWidthMm) / 2,
              footerLineY,
              footerWidthMm,
              footerHeightMm * footerScale,
              undefined,
              'FAST'
            )
          }
          pdf.line(
            MARGIN_MM,
            footerLineY,
            PAGE_WIDTH_MM - MARGIN_MM,
            footerLineY
          )
          pdf.text(
            `Página ${page} de ${totalPages}`,
            PAGE_WIDTH_MM - MARGIN_MM,
            footerLineY + 5,
            { align: 'right' }
          )
        }

        // O documento é serializado uma única vez: `pdf.save()` seguido de
        // `pdf.output('blob')` montaria e comprimiria o PDF inteiro duas vezes.
        // O download sai do mesmo blob que vai para o upload.
        const pdfBlob = pdf.output('blob')
        const downloadUrl = URL.createObjectURL(pdfBlob)
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = filename
        link.click()
        URL.revokeObjectURL(downloadUrl)

        return { success: true, blob: pdfBlob }
      } catch (error) {
        console.error('Error generating PDF:', error)
        return { success: false, blob: null }
      }
    },
    []
  )

  return { generatePDF }
}
